import { FlattenedMatter } from '@/database/aggregateTypes';
import { FirmDatabase } from '@/database/firmDatabase';
import { MatterData, MatterType } from '@shared/types';
import { AddEditMatterFormData } from '../types';
import { addOrUpdateInsuranceCases } from './addOrUpdateInsuranceCases';
import { addOrUpdateMatterContacts } from './addOrUpdateMatterContacts';
import { MissingFieldError } from './saveFormData';
import { assertUnreachable } from '@/utils/assertUnreachable';
import { constructMatterNameFromFormData } from '../constructMatterNameFromFormData';

export const updateExistingMatter = async (props: {
  db: FirmDatabase;
  matter: FlattenedMatter;
  formData: AddEditMatterFormData;
  onSubmitEventId: string;
}) => {
  const { db, matter, formData, onSubmitEventId } = props;

  if (!formData.state) {
    throw new MissingFieldError('State is required');
  }

  if (!formData.matterStatus) {
    throw new MissingFieldError('Matter status is required');
  }

  if (!formData.feeType) {
    throw new MissingFieldError('Fee type is required');
  }

  const matterType = matterTypeForForm(formData);

  // Update the contacts
  await addOrUpdateMatterContacts(
    db,
    matter.matterRef.id,
    formData,
    onSubmitEventId,
  );

  // Create the raw matter data
  const matterData: Partial<MatterData> = {
    matterType,
    responsibleAttorney: formData.responsibleAttorneyId || undefined,
    state: formData.state,
    status: formData.matterStatus,
  };

  if (!formData.matterName || formData.matterName?.trim() === '') {
    matterData.name = constructMatterNameFromFormData(formData);
  } else {
    matterData.name = formData.matterName.trim();
  }

  if (formData.description) {
    matterData.description = formData.description;
  }

  if (formData.leadSourceId) {
    matterData.leadSourceId = formData.leadSourceId;
  }

  if (formData.practiceAreaId) {
    matterData.practiceArea = formData.practiceAreaId;
  }

  if (formData.responsibleAttorneyId) {
    matterData.responsibleAttorney = formData.responsibleAttorneyId;
  }

  if (formData.otherAssigneeId) {
    matterData.otherAssignee = formData.otherAssigneeId;
  }

  if (formData.scope) {
    matterData.scope = formData.scope;
  }

  if (formData.matterSubStatusId) {
    matterData.subStatusId = formData.matterSubStatusId;
  }

  switch (formData.feeType) {
    case 'Insurance':
      // Insurance case fields are stored on an insuranceCases document.
      break;
    case 'Hourly':
      matterData.retainer = formData.retainer ?? undefined;
      matterData.responsibleAttyHourlyRate =
        formData.responsibleAttyHourlyRate ?? undefined;
      matterData.referralOriginator =
        formData.referralOriginatorId ?? undefined;
      matterData.engagementOriginator =
        formData.engagementOriginatorId ?? undefined;
      break;
    case 'FlatFee':
      matterData.flatFeeAmt = formData.flatFeeAmt ?? undefined;
      matterData.referralOriginator =
        formData.referralOriginatorId ?? undefined;
      matterData.engagementOriginator =
        formData.engagementOriginatorId ?? undefined;
      break;
    case 'Contingency':
      matterData.contingencyFees = {
        pctPreFilingOfLawsuit:
          formData.contingencyFees?.pctPreFilingOfLawsuit ?? undefined,
        pctUponFilingOfLawsuit:
          formData.contingencyFees?.pctUponFilingOfLawsuit ?? undefined,
        pctUponCommencementOfTrial:
          formData.contingencyFees?.pctUponCommencementOfTrial ?? undefined,
      };
      matterData.referralOriginator =
        formData.referralOriginatorId ?? undefined;
      matterData.engagementOriginator =
        formData.engagementOriginatorId ?? undefined;
      break;
    case `Unknown`:
      matterData.referralOriginator =
        formData.referralOriginatorId ?? undefined;
      matterData.engagementOriginator =
        formData.engagementOriginatorId ?? undefined;
      break;
    default:
      return assertUnreachable(formData);
  }
  await db.updateMatter(matter.id, matterData);

  if (formData.feeType === 'Insurance') {
    await addOrUpdateInsuranceCases({
      firmDb: db,
      formData,
      matterId: matter.id,
      onSubmitEventId,
    });
  }
};

const matterTypeForForm = (formData: AddEditMatterFormData): MatterType => {
  const feeType = formData.feeType;
  switch (feeType) {
    case 'Insurance':
      const provider = formData.insuranceProvider;
      switch (provider) {
        case 'arag':
          return 'ARAG';
        case 'metlife':
          return 'MetLife';
        case 'legalease':
          return 'LegalEase';
        case 'other':
          return 'OtherInsurance';
        default:
          return 'OtherInsurance';
      }
    case 'Unknown':
      return `Don't Know`;
    default:
      return feeType;
  }
};
